import { cloneDeep, flattenDeep, groupBy, orderBy } from "lodash"
import moment from "moment"
import React, { useMemo } from "react"
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native"
import Dash from "react-native-dash"
import Icon from "react-native-vector-icons/AntDesign"

import Base, { Firebase, Order } from "../../base"
import AppButton from "../common/AppButton"
import Gap from "../common/Gap"
import OrderDetailItemRow from "./OrderDetailItemRow"
import OrderDetailsItem from "./OrderDetailsItem"

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "GBP",
})

export default function OrderDetailsView(props) {
  const status = props.order?.currentStatus
  const isPending = status === Order.status.PENDING
  const isCompleted = Order.completedGroup.includes(status)

  const totalCost = formatter.format(props.order?.total ?? 0)

  function cardStyle() {
    if (Platform.OS !== "web") {
      return styles.card_full
    }
    return props.isMobile ? styles.card_sm : styles.card_rg
  }

  function acceptButtonColour() {
    if (status === Order.status.ACCEPTED) {
      return Base.colours.appGreen
    }
    return Base.colours.appRed
  }

  function callCustomer() {
    Base.functions.callNumber(props.order.customer.phoneNumber)
    Firebase.logEvent("order_called")
  }

  function buttons() {
    if (isCompleted || props.isFlooid) {
      return <View />
    }

    if (props.order.isUpdating) {
      return (
        <View style={[styles.footer, styles.loadingContainer]}>
          <ActivityIndicator size="small" color={"white"} />
        </View>
      )
    }

    if (status === Order.status.READY) {
      return (
        <View style={styles.footer}>
          <AppButton
            style={styles.completedActionButton}
            title={"Complete Order"}
            onPress={() => props.onAction(props.order, "positive")}
          />
        </View>
      )
    }

    return (
      <View style={styles.footer}>
        <AppButton
          style={styles.actionButton}
          title={isPending ? "Reject" : "Cancel Order"}
          negative={true}
          onPress={() => props.onAction(props.order, "negative")}
        />
        <Gap size={12} horizontal />
        <AppButton
          style={[
            styles.actionButton,
            { backgroundColor: acceptButtonColour() },
          ]}
          title={
            isPending
              ? "Accept"
              : status === Order.status.READY
              ? "Completed?"
              : "Ready"
          }
          onPress={() => props.onAction(props.order, "positive")}
        />
      </View>
    )
  }

  function detailTitle() {
    if (props.order?.type === "OAT" && props.order?.timeslot) {
      return "Delivery At"
    }
    if (props.order?.type === "Takeaway" && props.order?.timeslot) {
      return "Collecting At"
    }
    return "Table Number"
  }

  const basket = useMemo(() => props.order?.basket ?? [], [props])

  const orderedBasket = useMemo(() => {
    const list = cloneDeep(basket ?? [])

    if (!list?.length) {
      return []
    }

    const { withSku, withoutSku } = groupBy(list, ({ item }) =>
      item?.sku ? "withSku" : "withoutSku",
    )

    const withSkuGroupped = groupBy(
      orderBy(withSku ?? [], ["item.sku"], ["asc"]),
      ({ item }) => item.sku,
    )

    return flattenDeep([
      ...Object.values(withSkuGroupped || []),
      ...Object.values(withoutSku || []),
    ])
  }, [basket])

  if (!props.order) {
    return <View />
  }

  return (
    <View style={styles.main}>
      <View style={[cardStyle(), styles.card]}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Order Details</Text>
          <TouchableOpacity style={styles.headerButton} onPress={props.onClose}>
            <Icon name="close" size={24} color={Base.colours.appDarkBlue} />
          </TouchableOpacity>
        </View>
        {!props.order.basket ? (
          <View style={[styles.flexContainer, styles.loadingContainer]}>
            <ActivityIndicator size="large" color={Base.colours.appGrey} />
          </View>
        ) : (
          <View style={styles.flexContainer}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.content}>
                <View style={styles.contentRow}>
                  <OrderDetailsItem
                    title={detailTitle()}
                    value={
                      props.order.timeslot
                        ? moment(props.order.timeslot).format("HH:mm")
                        : `#${props.order.destination}`
                    }
                    style={{ color: Base.colours.appGreen }}
                  />
                  <OrderDetailsItem
                    title="Customer Name"
                    value={props.order.customer.firstName}
                  />
                </View>
                <View style={styles.contentRow}>
                  <OrderDetailsItem
                    title="Order Number"
                    value={"#" + props.order.reference}
                  />
                  <OrderDetailsItem
                    title="Customer Number"
                    value={props.order.customer.phoneNumber}
                    onPress={callCustomer}
                    style={styles.listItem}
                  />
                </View>
                <View style={styles.contentRow}>
                  <OrderDetailsItem
                    title="Order Submitted"
                    value={moment(props.order.orderedAt).format("HH:mm")}
                  />
                </View>
                <OrderDetailsItem
                  title="Customer Notes"
                  value={props.order.notes}
                  mainStyle={styles.fullWidth}
                  style={styles.notes}
                />
                <Gap size={15} />
                <Dash
                  style={styles.dashedLine}
                  dashColor={Base.colours.appBlack}
                  dashGap={11}
                  dashLength={11}
                  dashThickness={1}
                />
                <View style={styles.priceRow}>
                  <Text style={styles.itemCount}>
                    {props.order.quantity}{" "}
                    {props.order.quantity === 1 ? "item" : "items"}
                  </Text>
                  <View style={styles.totalWrap}>
                    <Text style={[Base.styles.body, styles.totalText]}>
                      Total
                    </Text>
                    <Text style={styles.itemCount}>{totalCost}</Text>
                  </View>
                </View>
                {props.order.promotions.map((promo, i) => {
                  return (
                    <View style={styles.promoItem} key={String(i)}>
                      <View style={styles.priceRow}>
                        <Text
                          style={[
                            styles.itemCount,
                            { color: Base.colours.appDarkBlue },
                          ]}
                        >
                          Promo:{" "}
                          {promo.internalName || promo.promotionName || ""}
                        </Text>
                        <Text style={Base.styles.body}>
                          {formatter.format(
                            promo.discount ?? -promo.amount ?? "0.0",
                          )}
                        </Text>
                      </View>
                    </View>
                  )
                })}
                {orderedBasket?.map((item, index) => {
                  return (
                    <OrderDetailItemRow
                      key={index}
                      item={item}
                      formatter={formatter}
                    />
                  )
                })}
              </View>
              <Gap size={24} />
            </ScrollView>
            {buttons()}
          </View>
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  main: {
    flex: 1,
    backgroundColor: "#00000097",
    alignItems: "center",
    justifyContent: "center",
  },

  flexContainer: { flex: 1 },
  fullWidth: { width: "100%" },

  card: {
    backgroundColor: "white",
    marginRight: -1,
    borderRadius: 4,
    overflow: "hidden",
    justifyContent: "space-between",
  },

  card_sm: {
    width: "90%",
    height: "90%",
  },

  card_rg: {
    width: 600,
    height: "90%",
  },

  card_full: {
    width: "100%",
    height: "100%",
  },

  header: {
    height: 64,
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: Base.colours.appYellow,
    alignItems: "center",
    paddingHorizontal: 16,
  },

  headerTitle: {
    color: Base.colours.appDarkBlue,
    fontFamily: Base.fonts.appExtraBold,
    fontSize: 24,
  },

  content: {
    marginHorizontal: 16,
    marginTop: 16,
  },

  contentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },

  dashedLine: {
    height: 1,
    overflow: "hidden",
  },

  priceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginVertical: 12,
  },

  itemCount: {
    color: Base.colours.appGreen,
    fontFamily: Base.fonts.appExtraBold,
    fontSize: 16,
  },

  footer: {
    backgroundColor: Base.colours.appDarkBlue,
    height: 56,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },

  actionButton: {
    width: 130,
    height: 36,
  },

  completedActionButton: {
    width: 190,
    height: 36,
    backgroundColor: Base.colours.appGrey,
  },
  loadingContainer: { justifyContent: "center" },
  listItem: { textDecorationLine: "underline" },
  notes: { color: Base.colours.appRed, marginVertical: 10 },
  totalWrap: { flexDirection: "row" },
  totalText: { marginRight: 9 },
  promoItem: { marginBottom: 24 },
})
